/**
 * *Tidy tags*: a summary of what would change, every row of it if you want to look, and one
 * destructive button.
 *
 * ── Why this is real DOM and not `showConfirmDialog` ─────────────────────────
 *
 * The toolkit has a confirmation helper and it is the wrong tool here, for a reason that is
 * about safety rather than taste. `showConfirmDialog` takes a **message string**, and its own
 * documentation says an HTML caller must escape any user-supplied substring itself.
 *
 * Every string in this dialog is a **tag name**, and a tag name is text somebody typed into a
 * field. Composing `"<b>#${tag}</b> → <b>#${other}</b>"` and handing it to a helper that
 * assigns markup makes a tag the injection vector — on the origin holding the session cookie,
 * where injected script does not need to read the cookie to act as the user. That is exactly
 * the hazard `BookmarkStore.ALLOWED_SCHEMES` refuses `javascript:` URLs over, arriving through
 * a different door.
 *
 * So this builds nodes with `element()` and `button()` and assigns `textContent`, which never
 * interprets anything. `Dom.kt`'s header makes the argument in general; this file is the place
 * where the general argument has a specific attacker.
 *
 * The string helper could not have carried the drill-down anyway. A message is a paragraph;
 * this needs a scroll region with a checkbox per row, which is a surface rather than a
 * sentence.
 *
 * ── What it wears ────────────────────────────────────────────────────────────
 *
 * The toolkit's own `.dt-modal` / `.dt-modal-backdrop` classes, exactly as the sign-in dialog
 * does — so it follows the toolkit's chrome, its corner radius, its accent and every theme's
 * treatment for free, and keeps following them after a lunula upgrade. *Apply* wears
 * `.dt-modal-btn-confirm.dt-destructive`, the toolkit's own opt-in for a dangerous action: a
 * quiet danger-tinted surface rather than a solid red, which is what the toolkit settled on
 * after a solid fill read as alarming for routine work.
 *
 * ── The three things it will not do ──────────────────────────────────────────
 *
 *  · **It does not open until the plan is ready.** No spinner inside a modal somebody has to
 *    dismiss to escape from — the button that opens this says *Planning…* and stays put. See
 *    `SettingsBackingViewModel`, where the decision lives.
 *  · **It does not ask twice.** *Apply* applies. The summary above it was computed by the
 *    server against the live collection and *Undo last tidy* is one press away; a confirmation
 *    in front of a previewed, reversible action is a dialog people learn to dismiss without
 *    reading, and the next one is the one that mattered.
 *  · **It does not do arithmetic.** Unticking a row asks the server what the new selection
 *    would cost. The number that matters — how many bookmarks would be left with no tags at
 *    all — needs every bookmark's tag rows, which this browser does not have and should not
 *    fetch in order to draw a line of summary.
 */
package se.soderbjorn.lunapin.web

import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent
import se.soderbjorn.lunapin.client.viewmodel.SettingsBackingViewModel
import se.soderbjorn.lunapin.client.viewmodel.SettingsBackingViewModel.State
import se.soderbjorn.lunapin.clientserver.TidyDrop
import se.soderbjorn.lunapin.clientserver.TidyImpact
import se.soderbjorn.lunapin.clientserver.TidyMerge
import se.soderbjorn.lunapin.clientserver.TidyOrphan
import se.soderbjorn.lunapin.clientserver.TidySources

/** The id the card's `aria-labelledby` points at. */
private const val TITLE_ID = "lp-tidy-title"

/**
 * The dialog, built once when a plan arrives and updated in place until it closes.
 *
 * Built once rather than repainted for the reason `SettingsView` gives about its own controls,
 * and here it is sharper: every recount republishes the whole state, and a rebuild would put a
 * fresh checkbox under the pointer that had just pressed one — losing its focus, and with it
 * any chance of working through the list from the keyboard.
 *
 * @param model the settings window's backing view model. Every gesture goes to it; nothing is
 *   decided here, including which rows are ticked.
 */
internal class TidyTagsDialog(private val model: SettingsBackingViewModel) {

    private val backdrop = element("div", "dt-modal-backdrop lp-tidy-backdrop")
    private val card = element("div", "dt-modal lp-tidy")

    private val headline = element("div", "lp-tidy-headline")
    private val counts = element("div", "lp-tidy-counts")
    private val orphanLine = element("div", "lp-tidy-orphans")
    private val message = element("div", "lp-tidy-message")

    private val disclosure = button("", "lp-tidy-disclosure") { toggleRows() }
    private val rows = element("div", "lp-tidy-rows")

    private val cancelButton = button("Cancel", "dt-modal-btn dt-modal-btn-cancel") {
        model.onTidyDismissed()
    }
    private val applyButton =
        button("Apply", "dt-modal-btn dt-modal-btn-confirm dt-destructive lp-tidy-apply") {
            model.onTidyApplyRequested()
        }

    /**
     * One checkbox per proposal, by the row it belongs to.
     *
     * Held so a repaint can write the ticks the model holds back into controls that already
     * exist. The key is the row's identity as the server stated it — `from→to` for a merge and
     * the tag for a drop — because a plan is a list and an index would stop meaning anything
     * the day the list is filtered.
     */
    private val boxes = mutableMapOf<String, HTMLInputElement>()

    /** Whether the drill-down is open. View-local: nothing outside this cares. */
    private var expanded = false

    /**
     * How many proposals the plan holds.
     *
     * Kept because the disclosure's label carries it and [toggleRows] repaints that label
     * without a state to read it from. Fixed for the life of the dialog: unticking recounts,
     * it never re-plans.
     */
    private var rowCount = 0

    /** Installed on `document` while this is up, and removed on the way out. */
    private var keyHandler: ((Event) -> Unit)? = null

    /** Whatever had focus when this opened, so it can have it back. See [dispose]. */
    private val opener: HTMLElement? = document.activeElement as? HTMLElement

    /**
     * Put it in the document and draw [dialog].
     *
     * The rows are built here rather than on the first render, because the plan does not
     * change while the dialog is open: unticking recounts, it does not re-plan. Re-planning
     * would spend the operator's key a second time to answer a question nobody asked.
     */
    fun mount(dialog: State.TidyDialog) {
        card.setAttribute("role", "dialog")
        card.setAttribute("aria-modal", "true")
        card.setAttribute("aria-labelledby", TITLE_ID)

        val title = element("h2", "dt-modal-title", "Tidy tags")
        title.id = TITLE_ID
        card.appendChild(title)

        val summary = element("div", "lp-tidy-summary")
        summary.appendChild(headline)
        summary.appendChild(counts)
        summary.appendChild(orphanLine)
        card.appendChild(summary)

        rowCount = dialog.rows
        disclosure.setAttribute("aria-expanded", "false")
        paintDisclosure()
        card.appendChild(disclosure)
        rows.hidden = true
        buildRows(dialog)
        card.appendChild(rows)
        card.appendChild(message)

        val footer = element("div", "dt-modal-buttons lp-tidy-footer")
        footer.appendChild(cancelButton)
        footer.appendChild(applyButton)
        card.appendChild(footer)

        backdrop.appendChild(card)
        document.body?.appendChild(backdrop)

        backdrop.addEventListener("click", { ev ->
            if (ev.target === backdrop) model.onTidyDismissed()
        })
        val handler: (Event) -> Unit = { ev -> onKeyDown(ev as KeyboardEvent) }
        document.addEventListener("keydown", handler)
        keyHandler = handler

        render(dialog)
        // Cancel rather than Apply, deliberately: the destructive button must not be one
        // stray Return away from the moment this opens.
        cancelButton.focus()
    }

    /** Take it out of the document and leave nothing behind. */
    fun dispose() {
        backdrop.parentNode?.removeChild(backdrop)
        // Removed rather than left to no-op: a settings window can open a dozen of these in a
        // session, and a pile of dead key handlers on `document` is one per dialog ever opened.
        keyHandler?.let { document.removeEventListener("keydown", it) }
        keyHandler = null
        opener?.takeIf { document.contains(it) }?.focus()
    }

    /**
     * Paint one state.
     *
     * Everything read here is derived from the shared state rather than tracked locally, so
     * there is no private copy of "am I applying" to fall out of step with the model that
     * actually knows.
     */
    fun render(dialog: State.TidyDialog) {
        val impact = dialog.impact
        headline.textContent = "${impact.tagsBefore} → ${impact.tagsAfter} tags"
        // Dimmed rather than blanked while a recount is in flight: a total that vanishes on
        // every tick reads as the plan having become unknowable, where a stale number that is
        // visibly settling reads as what it is.
        counts.textContent = countsLine(impact)
        counts.classList.toggle("lp-tidy-counting", dialog.isCounting)
        orphanLine.textContent = orphanSentence(impact)
        orphanLine.classList.toggle("lp-tidy-orphans-warn", impact.orphaned > 0)

        dialog.plan.merges.forEach { merge ->
            boxes[mergeKey(merge)]?.checked =
                dialog.tickedMerges.any { it.from == merge.from && it.to == merge.to }
        }
        dialog.plan.drops.forEach { drop ->
            boxes[dropKey(drop)]?.checked = drop.tag in dialog.tickedDrops
        }

        applyButton.textContent = if (dialog.isApplying) "Applying…" else "Apply"
        // Disabled while a change is in flight, and while nothing is ticked — an *Apply* over
        // an empty selection is a button whose only outcome is a request that does nothing.
        applyButton.disabled = dialog.isApplying || dialog.isEmpty
        cancelButton.disabled = dialog.isApplying

        message.textContent = dialog.message ?: ""
        message.hidden = dialog.message == null
    }

    // ── Building ─────────────────────────────────────────────────────────────

    /**
     * Every proposal, merges first.
     *
     * **One scroll region inside this dialog, not a second window.** The drill-down is where
     * somebody decides that `#restaurants` really should not fold into `#restaurant`, which is
     * a decision made *against* the summary above it — a separate window would put the number
     * they are deciding about on the other screen.
     *
     * Built whole rather than virtualised. A plan over the measured collection is a few hundred
     * rows of two spans and a checkbox, which a browser lays out without noticing; the day a
     * plan is genuinely long enough to matter, the answer is a shorter plan.
     */
    private fun buildRows(dialog: State.TidyDialog) {
        dialog.plan.merges.forEach { merge ->
            rows.appendChild(
                row(
                    key = mergeKey(merge),
                    body = mergeBody(merge),
                    detail = detailFor(merge.source, merge.uses),
                    onToggled = { ticked ->
                        model.onTidyMergeTicked(from = merge.from, to = merge.to, ticked = ticked)
                    },
                ),
            )
        }
        dialog.plan.drops.forEach { drop ->
            rows.appendChild(
                row(
                    key = dropKey(drop),
                    body = dropBody(drop),
                    detail = detailFor(drop.source, drop.uses),
                    onToggled = { ticked -> model.onTidyDropTicked(tag = drop.tag, ticked = ticked) },
                ),
            )
        }
    }

    /**
     * One tickable row: a checkbox, what would happen, and why it is being suggested.
     *
     * A `<label>` wrapping the box so the whole row is the hit target, which matters over a
     * list somebody is working down: a checkbox is a twelve-pixel target and a row is not.
     */
    private fun row(
        key: String,
        body: HTMLElement,
        detail: String,
        onToggled: (Boolean) -> Unit,
    ): HTMLElement {
        val label = element("label", "lp-tidy-row")
        val box = document.createElement("input") as HTMLInputElement
        box.type = "checkbox"
        box.className = "lp-tidy-check"
        box.addEventListener("change", { onToggled(box.checked) })
        boxes[key] = box
        label.appendChild(box)
        val text = element("div", "lp-tidy-row-text")
        text.appendChild(body)
        text.appendChild(element("div", "lp-tidy-row-why", detail))
        label.appendChild(text)
        return label
    }

    /**
     * `#election → #elections`, as three nodes.
     *
     * Three rather than one string with an arrow in it, because the two tags are `<code>` and
     * the arrow is not — and because building it out of nodes is what keeps a tag name from
     * ever being adjacent to markup. See the file header.
     */
    private fun mergeBody(merge: TidyMerge): HTMLElement {
        val line = element("div", "lp-tidy-row-body")
        line.appendChild(element("code", "lp-tidy-tag lp-tidy-tag-going", merge.from))
        line.appendChild(element("span", "lp-tidy-arrow", "→"))
        line.appendChild(element("code", "lp-tidy-tag", merge.to))
        return line
    }

    /** `#foreign-accent-syndrome`, and the word for what happens to it. */
    private fun dropBody(drop: TidyDrop): HTMLElement {
        val line = element("div", "lp-tidy-row-body")
        line.appendChild(element("code", "lp-tidy-tag lp-tidy-tag-going", drop.tag))
        line.appendChild(element("span", "lp-tidy-arrow", "removed"))
        return line
    }

    /**
     * Open or close the drill-down, and relabel the control that did it.
     *
     * The label is written here as well as in [render], because **no state change is coming**:
     * whether a list is expanded is view-local, exactly as which settings tab is showing is.
     * The model is not told and should not be — it is not a fact a phone would need, and it is
     * not a fact worth persisting.
     */
    private fun toggleRows() {
        expanded = !expanded
        rows.hidden = !expanded
        // The card is a sentence and two buttons while the list is shut, and a wall of several
        // hundred rows while it is open. Those want completely different widths, so the width
        // follows the disclosure rather than being set once for the worse case: a summary
        // stretched across a wide screen is unreadable, and a four-hundred-row list in a
        // 38rem column is a scroll nobody reaches the end of. See `.lp-tidy-wide`.
        card.classList.toggle("lp-tidy-wide", expanded)
        disclosure.setAttribute("aria-expanded", expanded.toString())
        paintDisclosure()
    }

    /** *▸ See all 393 changes*, or the same sentence the other way round. */
    private fun paintDisclosure() {
        val verb = if (expanded) "▾ Hide" else "▸ See"
        val noun = if (rowCount == 1) "change" else "changes"
        disclosure.textContent = "$verb all $rowCount $noun"
    }

    // ── Keys ─────────────────────────────────────────────────────────────────

    /**
     * Escape and Tab, which are the two keys a modal owns.
     *
     * **Escape only when this is the topmost dialog**, established by checking that no later
     * `.dt-modal-backdrop` was appended after ours — the same test `SignInDialog` makes, and
     * for the same reason: there is no shared dialog registry in the toolkit, so a
     * confirmation opened over this one would otherwise take both down on one press.
     */
    private fun onKeyDown(ev: KeyboardEvent) {
        when (ev.key) {
            "Escape" -> {
                if (!isTopmostModal()) return
                ev.preventDefault()
                model.onTidyDismissed()
            }

            "Tab" -> trapFocus(ev)
        }
    }

    private fun isTopmostModal(): Boolean {
        val stack = document.querySelectorAll(".dt-modal-backdrop")
        return stack.length == 0 || stack.item(stack.length - 1) === backdrop
    }

    /**
     * Keep Tab inside the card. `SignInDialog`'s mechanism, and its reasoning.
     *
     * Only the two wrap points are handled; everything between them is the browser's own tab
     * order, which is already correct because the DOM order is the reading order. It matters
     * more here than there: the drill-down is a list somebody works down with the keyboard,
     * and tabbing off the end of it into the settings window behind would lose the place.
     */
    private fun trapFocus(ev: KeyboardEvent) {
        val stops = focusable()
        if (stops.isEmpty()) return
        val active = document.activeElement
        if (ev.shiftKey && active === stops.first()) {
            ev.preventDefault()
            stops.last().focus()
        } else if (!ev.shiftKey && active === stops.last()) {
            ev.preventDefault()
            stops.first().focus()
        }
    }

    /** Every enabled control in the card, in DOM order. Hidden rows are not stops. */
    private fun focusable(): List<HTMLElement> {
        val nodes = card.querySelectorAll("input, button")
        return (0 until nodes.length)
            .mapNotNull { nodes.item(it) as? HTMLElement }
            .filter { it.asDynamic().disabled != true }
            .filter { !rows.hidden || !rows.contains(it) }
    }
}

/** The identity of a merge row, for finding its checkbox again. */
private fun mergeKey(merge: TidyMerge): String = "merge:${merge.from}→${merge.to}"

/** And of a drop row. Prefixed so a tag called `merge:…` cannot collide with one. */
private fun dropKey(drop: TidyDrop): String = "drop:${drop.tag}"

/**
 * *685 removed · 21 merged · 4 812 tag rows gone*.
 *
 * Three numbers because they answer three different questions people actually ask: how much
 * smaller is the vocabulary, how much of that was folding rather than throwing away, and how
 * much of the *filing* changed. The third is the one that is easy to leave out and the one
 * that says whether this was a tidy or a demolition — a merge that removes two hundred tags
 * and eight tag rows has barely touched the collection.
 */
private fun countsLine(impact: TidyImpact): String {
    val rows = if (impact.tagRowsRemoved == 1L) {
        "1 tag row gone"
    } else {
        "${impact.tagRowsRemoved} tag rows gone"
    }
    return listOf("${impact.dropped} removed", "${impact.merged} merged", rows)
        .joinToString(" · ")
}

/**
 * What happens to the bookmarks, which is the sentence that decides whether this is pressable.
 *
 * Names the casualties rather than only counting them. *One bookmark would be left with no
 * tags* is a claim somebody has to take on trust; naming it is a fact they can check in three
 * seconds, and on the collection this feature was measured against the honest answer to
 * "remove all 685 singletons" is exactly one bookmark.
 *
 * The list is the server's and is already capped — see its `MAX_NAMED_ORPHANS` — so this draws
 * whatever came and says nothing about the rest, which the count above it already covers.
 */
private fun orphanSentence(impact: TidyImpact): String = when {
    impact.orphaned == 0L -> "Every bookmark keeps a tag."
    impact.orphanExamples.isEmpty() && impact.orphaned == 1L ->
        "1 bookmark would be left with no tags."
    impact.orphanExamples.isEmpty() ->
        "${impact.orphaned} bookmarks would be left with no tags."
    impact.orphaned == 1L ->
        "1 bookmark would be left with no tags: ${named(impact.orphanExamples)}"
    else ->
        "${impact.orphaned} bookmarks would be left with no tags, including " +
            named(impact.orphanExamples)
}

/** A handful of bookmarks by whichever of title and URL says most about them. */
private fun named(orphans: List<TidyOrphan>): String =
    orphans.joinToString(", ") { it.title.ifBlank { it.url } }

/**
 * Why a row is being suggested, in the fewest words that distinguish the four passes.
 *
 * The source is not decoration: it is what a reader trusts a row by. A singular/plural pair is
 * a rule that cannot be wrong about what it found; a shared-word cluster is a guess about what
 * a compound means; a model's suggestion is a third thing again. Saying which is what lets
 * somebody skim two hundred rows and slow down at the right twenty.
 */
private fun detailFor(source: String, uses: Long): String {
    val why = when (source) {
        TidySources.PLURAL -> "singular/plural pair"
        TidySources.SINGLETON -> "used once"
        TidySources.STEM -> "shares a word with a much-used tag"
        TidySources.MODEL -> "suggested by the model"
        // A source from a newer server. Named as unknown rather than hidden: a row whose
        // reason cannot be shown is still a row, and drawing it without one would quietly
        // present a suggestion as if it were a rule.
        else -> source
    }
    val count = if (uses == 1L) "1 bookmark" else "$uses bookmarks"
    return "$why · $count"
}
